import React, { useContext } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Alert, DialogContentText, DialogTitle, Link } from "@carrotfertility/carotene-core"
import { Form, FormOnSubmitHandler } from "@carrotfertility/carotene-core-x"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import RequestCardWrapper from "#/pages/request-carrot-card-flow/requestCardWrapper"
import { InternationalAddressFields } from "#/services/common-forms"
import { reportError } from "#/utils/ErrorReporting"
import { RequestCardContext } from "#/pages/request-carrot-card-flow"
import { TtcPaths } from "#/utils/Paths"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useMutation } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import { MemberInternationalAddress } from "#/lib/carrot-api/types/Address"
import { RequestCardInternationalAddressForm } from "#/pages/request-carrot-card-flow/steps/AirwallexAddressForm/request-card-international-address-form"
import { UsaStates } from "#/utils/UsaStateOptions"

const AUSTRALIAN_STATE_CODES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"]
const US_STATE_CODES = UsaStates.map((state) => state.id)
const US_ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/

export function RequestCardInternationalAddress() {
  const { canGoBack, send } = useStateMachine(null)
  const showInformationScreen = useContext(RequestCardContext).showInformationScreen
  const { employeeId, internationalCountry, internationalAddressLine, city, state, zip, refreshUserInfo } =
    useCurrentUser()
  const intl = useIntl()

  const updateInternationalAddress = useMutation({
    mutationFn: (addressFields: MemberInternationalAddress) => carrotClient.updateMemberCardAddress(addressFields),
    onSuccess: () => {
      refreshUserInfo()
      send("")
    }
  })

  const onSubmit: FormOnSubmitHandler<InternationalAddressFields> = async (fieldValues) => {
    try {
      if (fieldValues.internationalCountry.value === "AU" && !AUSTRALIAN_STATE_CODES.includes(fieldValues.state)) {
        throw new Error(
          intl.formatMessage({ defaultMessage: "Please enter a valid Australian state code (e.g., NSW, VIC, QLD)" })
        )
      }

      if (fieldValues.internationalCountry.value === "US") {
        if (fieldValues.state && !US_STATE_CODES.includes(fieldValues.state.toUpperCase())) {
          throw new Error(
            intl.formatMessage({
              defaultMessage: "Please enter a valid two-letter US state code (e.g., CA, NY, NJ)"
            })
          )
        }
        if (fieldValues.zip && !US_ZIP_CODE_REGEX.test(fieldValues.zip)) {
          throw new Error(
            intl.formatMessage({
              defaultMessage: "Please enter a valid US zip code (5 digits or 5+4 format, e.g., 12345 or 12345-6789)"
            })
          )
        }
      }

      await updateInternationalAddress.mutateAsync({
        ...fieldValues,
        internationalCountry: fieldValues.internationalCountry.value
      })
    } catch (error) {
      error.message =
        `Error for employeeId: "${employeeId}" while confirming Carrot Card® international address: RAW:` +
        error.message
      reportError(error)
    }
  }
  return (
    <Form onSubmit={onSubmit} mode="onChange">
      <RequestCardWrapper showBackButton={canGoBack || showInformationScreen} showContinueButton>
        <DialogTitle>
          <FormattedMessage defaultMessage="Confirm your address" />
        </DialogTitle>
        <DialogContentText marginBottom={(theme) => theme.tokens.spacing.xl}>
          <FormattedMessage defaultMessage="Please confirm or update your address to receive a Carrot Card." />
        </DialogContentText>
        <RequestCardInternationalAddressForm
          defaultValues={{ internationalAddressLine, internationalCountry, city, state, zip }}
          stateValidation={(country: { value: string } | undefined, state: string) => {
            if (country?.value === "AU") {
              if (!AUSTRALIAN_STATE_CODES.includes(state)) {
                return intl.formatMessage({
                  defaultMessage: "Please enter a valid Australian state code (e.g., NSW, VIC, QLD)"
                })
              }
            }
            if (country?.value === "US") {
              if (state && !US_STATE_CODES.includes(state.toUpperCase())) {
                return intl.formatMessage({
                  defaultMessage: "Please enter a valid two-letter US state code (e.g., CA, NY, NJ)"
                })
              }
            }
            return true
          }}
          zipValidation={(country: { value: string } | undefined, zip: string) => {
            if (country?.value === "US") {
              if (zip && !US_ZIP_CODE_REGEX.test(zip)) {
                return intl.formatMessage({
                  defaultMessage: "Please enter a valid US zip code (5 digits or 5+4 format, e.g., 12345 or 12345-6789)"
                })
              }
            }
            return true
          }}
        />
        {updateInternationalAddress.isError && (
          <Alert severity="error">
            <FormattedMessage
              defaultMessage="An unexpected error occurred. Please <link>contact us</link> for assistance."
              values={{
                link: (linkContent) => (
                  <Link color="inherit" href={TtcPaths.TTC_CARE_NAV} target={"_blank"}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Alert>
        )}
      </RequestCardWrapper>
    </Form>
  )
}
